import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMessaging } from "../../context/MessagingContext";
import NewMessageDropdown from "./NewMessageDropdown";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const GroupAvatarIcon = () => (
  <svg
    className="group-avatar-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="6.6" r="2.5" fill="currentColor" />
    <path
      d="M8.6 13.3c.8-1.3 2-2 3.4-2s2.6.7 3.4 2c.6.9.9 1.9 1 3v.8H7.6v-.8c.1-1.1.4-2.1 1-3z"
      fill="currentColor"
    />
    <circle cx="6.4" cy="9.6" r="2.2" fill="currentColor" />
    <path
      d="M2.4 16.5c.1-.9.4-1.7.9-2.4.7-1.1 1.7-1.7 3.1-1.7.8 0 1.5.2 2.1.6-.5.5-.9 1.1-1.3 1.7-.3.5-.5 1.1-.7 1.8H2.4z"
      fill="currentColor"
    />
    <circle cx="17.6" cy="9.6" r="2.2" fill="currentColor" />
    <path
      d="M21.6 16.5c-.1-.9-.4-1.7-.9-2.4-.7-1.1-1.7-1.7-3.1-1.7-.8 0-1.5.2-2.1.6.5.5.9 1.1 1.3 1.7.3.5.5 1.1.7 1.8h4.1z"
      fill="currentColor"
    />
  </svg>
);

const SortableConversationItem = ({
  conversation,
  isSelected,
  hasUnread,
  isPinned,
  onSelectConversation,
  onPinClick,
  formatTimeAgo,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: conversation.conversation_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`conversation-item ${isSelected ? "selected" : ""} ${
        hasUnread ? "unread" : ""
      } ${isPinned ? "pinned" : ""} ${isDragging ? "dragging" : ""}`}
      onClick={() => onSelectConversation(conversation)}
    >
      <div
        className="conversation-drag-handle"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
      >
        ⋮⋮
      </div>
      <div className="conversation-avatar">
        {conversation.type === "group"
          ? <GroupAvatarIcon />
          : conversation.display_name?.charAt(0).toUpperCase() || "?"}
      </div>

      <div className="conversation-details">
        <div className="conversation-header-row">
          <div className="conversation-name">{conversation.display_name}</div>
          <div className="conversation-header-actions">
            <button
              type="button"
              className={`conversation-pin-btn ${isPinned ? "pinned" : ""}`}
              onClick={onPinClick}
              aria-label={isPinned ? "Unpin conversation" : "Pin conversation"}
              title={isPinned ? "Unpin" : "Pin"}
            >
              📌
            </button>
            <div className="conversation-time">
              {formatTimeAgo(conversation.last_message_at)}
            </div>
          </div>
        </div>

        <div className="conversation-message-row">
          <div className="conversation-last-message">
            {conversation.last_message_text || "No messages yet"}
          </div>
          {hasUnread && (
            <div className="conversation-unread-badge">
              {conversation.unread_count}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConversationItem = ({
  conversation,
  isSelected,
  hasUnread,
  isPinned,
  onSelectConversation,
  onPinClick,
  formatTimeAgo,
}) => {
  return (
    <div
      className={`conversation-item ${isSelected ? "selected" : ""} ${
        hasUnread ? "unread" : ""
      } ${isPinned ? "pinned" : ""}`}
      onClick={() => onSelectConversation(conversation)}
    >
      <div className="conversation-avatar">
        {conversation.type === "group"
          ? <GroupAvatarIcon />
          : conversation.display_name?.charAt(0).toUpperCase() || "?"}
      </div>

      <div className="conversation-details">
        <div className="conversation-header-row">
          <div className="conversation-name">{conversation.display_name}</div>
          <div className="conversation-header-actions">
            <button
              type="button"
              className={`conversation-pin-btn ${isPinned ? "pinned" : ""}`}
              onClick={onPinClick}
              aria-label={isPinned ? "Unpin conversation" : "Pin conversation"}
              title={isPinned ? "Unpin" : "Pin"}
            >
              📌
            </button>
            <div className="conversation-time">
              {formatTimeAgo(conversation.last_message_at)}
            </div>
          </div>
        </div>

        <div className="conversation-message-row">
          <div className="conversation-last-message">
            {conversation.last_message_text || "No messages yet"}
          </div>
          {hasUnread && (
            <div className="conversation-unread-badge">
              {conversation.unread_count}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConversationList = ({ onSelectConversation, selectedConversationId }) => {
  const {
    conversations,
    loading,
    searchConversations,
    pinConversation,
    reorderPinnedConversations,
  } = useMessaging();
  const isNarrowLayout = useMediaQuery("(max-width: 768px)");
  const [showNewMessageDropdown, setShowNewMessageDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [pinnedOrder, setPinnedOrder] = useState([]);

  // Filter conversations based on search term
  const filteredConversations = useMemo(() => {
    if (searchResults) {
      return searchResults;
    }

    if (!searchTerm.trim()) {
      return conversations;
    }

    const term = searchTerm.toLowerCase();
    return conversations.filter(
      (conv) =>
        conv.display_name?.toLowerCase().includes(term) ||
        conv.last_message_text?.toLowerCase().includes(term) ||
        conv.participants?.some(
          (p) =>
            p.name?.toLowerCase().includes(term) ||
            p.email?.toLowerCase().includes(term),
        ),
    );
  }, [conversations, searchTerm, searchResults]);

  // Split into pinned and unpinned (only when not searching)
  const { pinnedConversations, unpinnedConversations } = useMemo(() => {
    if (searchResults) {
      return {
        pinnedConversations: [],
        unpinnedConversations: filteredConversations,
      };
    }
    const pinned = filteredConversations.filter(
      (c) => c.is_pinned === true || c.is_pinned === 1,
    );
    const unpinned = filteredConversations.filter(
      (c) => !c.is_pinned && c.is_pinned !== 1,
    );
    return { pinnedConversations: pinned, unpinnedConversations: unpinned };
  }, [filteredConversations, searchResults]);

  // Sync pinned order when conversations change
  useEffect(() => {
    if (pinnedConversations.length > 0) {
      const sorted = [...pinnedConversations].sort(
        (a, b) => (a.pinned_order ?? 999) - (b.pinned_order ?? 999),
      );
      setPinnedOrder(sorted.map((c) => c.conversation_id));
    } else {
      setPinnedOrder([]);
    }
  }, [
    pinnedConversations.length,
    pinnedConversations
      .map((c) => `${c.conversation_id}-${c.pinned_order}`)
      .join(","),
  ]);

  // Search conversations on server if search term is long enough
  useEffect(() => {
    if (searchTerm.trim().length >= 3 && searchConversations) {
      const timeoutId = setTimeout(async () => {
        try {
          const response = await searchConversations(searchTerm);
          if (response.success) {
            setSearchResults(response.conversations);
          }
        } catch (error) {
          console.error("Error searching conversations:", error);
          setSearchResults(null);
        }
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults(null);
    }
  }, [searchTerm, searchConversations]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pinnedOrder.indexOf(active.id);
    const newIndex = pinnedOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(pinnedOrder, oldIndex, newIndex);
    const previousOrder = [...pinnedOrder];
    setPinnedOrder(newOrder);
    try {
      await reorderPinnedConversations(newOrder);
    } catch (error) {
      setPinnedOrder(previousOrder);
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const renderConversationItem = (conversation, useSortable = false) => {
    const isSelected = conversation.conversation_id === selectedConversationId;
    const hasUnread = conversation.unread_count > 0;
    const isPinned =
      conversation.is_pinned === true || conversation.is_pinned === 1;

    const handlePinClick = (e) => {
      e.stopPropagation();
      pinConversation(conversation.conversation_id, !isPinned);
    };

    const props = {
      conversation,
      isSelected,
      hasUnread,
      isPinned,
      onSelectConversation,
      onPinClick: handlePinClick,
      formatTimeAgo,
    };

    if (useSortable) {
      return (
        <SortableConversationItem
          key={conversation.conversation_id}
          {...props}
        />
      );
    }
    return <ConversationItem key={conversation.conversation_id} {...props} />;
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-loading">
          <div className="spinner"></div>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <div className="conversation-list-header-top">
          <h2>Messages</h2>
          <button
            type="button"
            className="new-message-btn"
            onClick={() => setShowNewMessageDropdown(true)}
            aria-label="New Message"
          >
            + New
          </button>
        </div>
        <div className="conversation-list-search">
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="conversation-search-input"
          />
        </div>
      </div>

      {showNewMessageDropdown && (
        <NewMessageDropdown
          isOpen={showNewMessageDropdown}
          onClose={() => setShowNewMessageDropdown(false)}
        />
      )}

      {filteredConversations.length === 0 && !loading ? (
        <div className="no-conversations">
          <span className="no-conv-icon">💬</span>
          <h3>
            {searchTerm ? "No conversations found" : "No conversations yet"}
          </h3>
          <p>
            {searchTerm
              ? "Try a different search term"
              : "Start a conversation with artists, bar owners, or admins"}
          </p>
        </div>
      ) : searchResults ? (
        <>
          {filteredConversations.map((conv) =>
            renderConversationItem(conv, false),
          )}
        </>
      ) : isNarrowLayout ? (
        <div className="conversation-list-items">
          {pinnedConversations.length > 0 &&
            pinnedConversations
              .sort(
                (a, b) => (a.pinned_order ?? 999) - (b.pinned_order ?? 999),
              )
              .map((conv) => renderConversationItem(conv, false))}
          {unpinnedConversations.map((conv) =>
            renderConversationItem(conv, false),
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="conversation-list-items">
            {pinnedConversations.length > 0 && (
              <SortableContext
                items={pinnedOrder}
                strategy={verticalListSortingStrategy}
              >
                {pinnedConversations
                  .sort(
                    (a, b) => (a.pinned_order ?? 999) - (b.pinned_order ?? 999),
                  )
                  .map((conv) => renderConversationItem(conv, true))}
              </SortableContext>
            )}
            {unpinnedConversations.map((conv) =>
              renderConversationItem(conv, false),
            )}
          </div>
        </DndContext>
      )}
    </div>
  );
};

export default ConversationList;
